import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createProduct, getProduct, updateProduct } from '@/services/dbService';
import { ClothingItemFormData, ClothingItem, ShoeItem } from '@/models/types';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { 
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { UploadCloudIcon, X, Image as ImageIcon, Clock, User, ExternalLink, Tag, Loader2, AlertTriangle } from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { Badge } from '@/components/ui/badge';
import { getProxiedImageUrl } from '@/services/imageProxyService';
import { fetchAffiliateConversion } from '@/services/productsService';
import { useLanguage } from '@/i18n';

const defaultFormData: ClothingItemFormData = {
  name: '',
  brand: '',
  price: 0,
  link: '',
  category: '',
  batch: '',
  search_aliases: '',
  image: null,
  created_by: '',
  order: undefined as unknown as number | undefined,
};



// Simple category list
const categories = [
  'Shoes',
  'Hoodies/Tops',
  'T-Shirts',
  'Jackets',
  'Pants/Bottoms',
  'Hats',
  'Accessories',
  'Decor',
  'Other'
];

const ItemForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState<ClothingItemFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [item, setItem] = useState<ClothingItem | ShoeItem | null>(null);
  const [creationInfo, setCreationInfo] = useState<{
    createdBy: string;
    createdAt: string;
    updatedAt: string | null;
  } | null>(null);

  const [brands, setBrands] = useState<string[]>([]);
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [filteredBrands, setFilteredBrands] = useState<string[]>([]);
  const brandInputRef = useRef<HTMLInputElement>(null);
  

  const [convertingLink, setConvertingLink] = useState(false);
  const [originalLink, setOriginalLink] = useState('');
  const [linkConversionError, setLinkConversionError] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const { data: brandsData, error: brandsError } = await supabase
          .from('db')
          .select('brand')
          .not('brand', 'is', null);
        
        if (brandsError) throw brandsError;
        
        if (brandsData) {
          const uniqueBrands = Array.from(new Set(
            brandsData.map(item => item.brand).filter(Boolean)
          )).sort();
          
          setBrands(uniqueBrands as string[]);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        toast.error(t('toastBrandsFailed'));
      }
    };
    
    fetchBrands();
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchItem = async () => {
        try {
          setLoading(true);
          const itemData = await getProduct(id);
          setItem(itemData);
          setFormData({
            name: itemData.name,
            brand: itemData.brand,
            price: itemData.price,
            link: itemData.link,
            category: itemData.category || '',
            batch: itemData.batch,
            search_aliases: (itemData as any).search_aliases || '',
            image: null,
            created_by: itemData.created_by || '',
            order: typeof (itemData as any).order === 'number' ? (itemData as any).order : undefined
          });

          
          if (itemData.created_by) {
            const now = new Date().toISOString();
            setCreationInfo({
              createdBy: itemData.created_by,
              createdAt: itemData.created_at || now,
              updatedAt: itemData.updated_at || null
            });
          }
          
          setImagePreview(itemData.image);
        } catch (error) {
          console.error('Error fetching item:', error);
          toast.error(t('toastItemLoadFailed'));
          navigate('/admin/clothinglist');
        } finally {
          setLoading(false);
        }
      };

      fetchItem();
    } else if (!isEditMode && user) {
      setFormData(prev => ({
        ...prev,
        created_by: user.email || 'Unknown user'
      }));
    }
  }, [id, isEditMode, navigate, user]);

  useEffect(() => {
    if (formData.brand) {
      const filtered = brands.filter(brand => 
        brand.toLowerCase().includes(formData.brand.toLowerCase())
      );
      setFilteredBrands(filtered);
    } else {
      setFilteredBrands(brands);
    }
  }, [formData.brand, brands]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'price') {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0,
      });
    } else if (name === 'order') {
      setFormData({
        ...formData,
        [name]: value === '' ? undefined : (parseInt(value, 10) || 0),
      });
    } else if (name === 'brand') {
      setFormData({
        ...formData,
        [name]: value,
      });
      setShowBrandSuggestions(true);
    } else if (name === 'link') {
      setFormData({
        ...formData,
        [name]: value,
      });
      setOriginalLink(value);
      setLinkConversionError(false);
      
      if (value && value.trim() !== '' && !value.includes('affiliate.aliofficials.com')) {
        const timeoutId = setTimeout(() => {
          convertAffiliateLink();
        }, 1000);
        
        return () => clearTimeout(timeoutId);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleBrandSelect = (brand: string) => {
    setFormData({
      ...formData,
      brand: brand,
    });
    setShowBrandSuggestions(false);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    if (imagePreview) {
      if (imagePreview !== item?.image) {
        URL.revokeObjectURL(imagePreview);
      }
      
      setImagePreview(null);
      setFormData({
        ...formData,
        image: null,
      });
    }
  };

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.name || formData.name.trim() === '') {
      errors.push(t('validationNameRequired'));
    }
    
    if (formData.price <= 0) {
      errors.push(t('validationPriceGreater'));
    }
    
    if (!formData.category) {
      errors.push(t('validationCategoryRequired'));
    }
    
    if (!isEditMode && !formData.image) {
      errors.push(t('validationImageRequired'));
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }
    
    if (formData.link && 
        !formData.link.includes('affiliate.aliofficials.com') && 
        !linkConversionError && 
        !convertingLink) {
      await convertAffiliateLink();
    }

    try {
      setSaving(true);
      const formDataToSubmit = new FormData();
      
      // Create a copy of formData with the correct image value
      const submissionData = { ...formData };
      
      // If we're in edit mode and no new image was uploaded, use the existing image URL
      if (isEditMode && !formData.image && item?.image) {
        submissionData.image = item.image;
      }
      
      Object.entries(submissionData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (value instanceof File) {
            formDataToSubmit.append(key, value);
          } else if (typeof value === 'number') {
            formDataToSubmit.append(key, value.toString());
          } else {
            formDataToSubmit.append(key, value as string);
          }
        }
      });

      if (formData.image instanceof File) {
        formDataToSubmit.append('image', formData.image);
      }

      if (formData.link) {
        formDataToSubmit.set('link', formData.link);
      }

      let savedItem;
      if (id) {
        savedItem = await updateProduct(id, submissionData as any);
      } else {
        savedItem = await createProduct(submissionData);
      }

      toast.success(id ? t('itemSavedUpdated') : t('itemSavedCreated'));
      navigate('/admin/clothinglist');
    } catch (error: unknown) {
      console.error('Error saving item:', error);
      const message = error && typeof error === 'object' && 'message' in error
        ? String((error as { message: unknown }).message)
        : t('failedToSaveItem');
      toast.error(message || t('failedToSaveItem'));
    } finally {
      setSaving(false);
    }
  };

  const convertAffiliateLink = async () => {
    if (!formData.link || formData.link.trim() === '') {
      return;
    }

    if (formData.link.includes('affiliate.aliofficials.com')) {
      return;
    }

    try {
      setConvertingLink(true);
      setLinkConversionError(false);

      const affiliate = await fetchAffiliateConversion(formData.link);
      const rawLink = affiliate.rawlink || formData.link || '';

      if (rawLink) {
        setFormData(prev => ({
          ...prev,
          link: rawLink,
        }));
      } else {
        throw new Error('No raw link in response');
      }
    } catch (error) {
      console.error('Error converting link:', error);
      setLinkConversionError(true);
    } finally {
      setConvertingLink(false);
    }
  };

  const handleTabChange = async (value: string) => {
    if (formData.link && 
        !formData.link.includes('affiliate.aliofficials.com') && 
        !linkConversionError) {
      await convertAffiliateLink();
    }
    
    setActiveTab(value);
  };

  const cardPreview = useMemo(() => {
    const displayPrice = formData.price ? `$${formData.price.toFixed(2)}` : '$0.00';
    const displayCategory = formData.category || 'N/A';
    const displayQuality = formData.batch || '';
    
    return {
      name: formData.name || 'Product Name',
      price: displayPrice,
      category: displayCategory,
      quality: displayQuality,
      image: imagePreview,
      link: formData.link || '#',
      originalLink: originalLink
    };
  }, [formData, imagePreview, originalLink]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <span className="text-sm text-muted-foreground">{t('loadingItemDetails')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 animate-fade-in">
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/admin/clothinglist')}
          className="mr-2"
        >
          <X className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{isEditMode ? t('editItemTitle') : t('addItemTitle')}</h1>
          <p className="text-muted-foreground">
            {isEditMode ? t('editItemSubtitle') : t('addItemSubtitle')}
          </p>
        </div>
      </div>

      {isEditMode && creationInfo && (
        <Card className="bg-background/50 border border-border/50 shadow-subtle">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{t('createdByLabel')}</span>
                <span className="font-medium">{creationInfo.createdBy}</span>
              </div>
              
              {creationInfo.createdAt && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('createdAtLabel')}</span>
                  <span className="font-medium">
                    {formatDistanceToNow(new Date(creationInfo.createdAt), { addSuffix: true })}
                  </span>
                </div>
              )}
              
              {creationInfo.updatedAt && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('lastUpdatedLabel')}</span>
                  <span className="font-medium">
                    {formatDistanceToNow(new Date(creationInfo.updatedAt), { addSuffix: true })}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <Tabs 
          defaultValue="details" 
          value={activeTab} 
          onValueChange={handleTabChange}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">{t('tabDetails')}</TabsTrigger>
            <TabsTrigger value="images">{t('tabImages')}</TabsTrigger>
            <TabsTrigger value="preview">{t('tabPreview')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="space-y-4">
            <Card className="bg-background border border-border/50 shadow-subtle">
              <CardHeader>
                <CardTitle>{t('basicInformation')}</CardTitle>
                <CardDescription>{t('basicInformationDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('labelName')}</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder={t('placeholderProductName')}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="price">{t('labelPrice')}</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t('placeholderPrice')}
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2 relative">
                    <Label htmlFor="brand">{t('labelBrand')}</Label>
                    <Input
                      id="brand"
                      name="brand"
                      placeholder={t('placeholderBrand')}
                      value={formData.brand}
                      onChange={handleInputChange}
                      onFocus={() => setShowBrandSuggestions(true)}
                      onBlur={() => {
                        setTimeout(() => setShowBrandSuggestions(false), 200);
                      }}
                      ref={brandInputRef}
                    />
                    {showBrandSuggestions && filteredBrands.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-background border border-border rounded-md shadow-md max-h-48 overflow-y-auto">
                        {filteredBrands.map((brand, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 hover:bg-accent cursor-pointer"
                            onClick={() => handleBrandSelect(brand)}
                          >
                            {brand}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="link">{t('labelProductLink')}</Label>
                    <Input
                      id="link"
                      name="link"
                      placeholder={t('placeholderProductLink')}
                      value={formData.link}
                      onChange={handleInputChange}
                      className="flex-1"
                    />
                    {formData.link !== originalLink && originalLink && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('original')}: <span className="italic truncate inline-block max-w-xs">{originalLink}</span>
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="order">{t('labelOrder')}</Label>
                    <Input
                      id="order"
                      name="order"
                      type="number"
                      min="1"
                      placeholder={t('placeholderOrder')}
                      value={formData.order ?? ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="search_aliases">{t('labelSearchAliases')}</Label>
                  <Input
                    id="search_aliases"
                    name="search_aliases"
                    placeholder={t('placeholderSearchAliases')}
                    value={(formData as any).search_aliases || ''}
                    onChange={handleInputChange}
                    maxLength={200}
                  />
                  <p className="text-xs text-muted-foreground">{t('searchAliasesHint')}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="category">{t('labelCategory')}</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleSelectChange("category", value)}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder={t('selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/admin/clothinglist')}
                >
                  {t('btnCancel')}
                </Button>
                <Button 
                  type="button" 
                  onClick={() => handleTabChange('images')}
                  disabled={convertingLink}
                >
                  {t('btnNextUploadImages')}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="images" className="space-y-4">
            <Card className="bg-background border border-border/50 shadow-subtle">
              <CardHeader>
                <CardTitle>{t('itemImage')}</CardTitle>
                <CardDescription>
                  {t('itemImageDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center w-full">
                  <Label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer bg-background/50 hover:bg-background/80 border-border/50 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <UploadCloudIcon className="w-10 h-10 mb-3 text-muted-foreground" />
                      <p className="mb-2 text-sm text-muted-foreground">
                        <span className="font-semibold">{t('clickToUpload')}</span> {t('orDragAndDrop')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('uploadFormats')}
                      </p>
                    </div>
                    <Input
                      id="image-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Label>
                </div>
                
                {imagePreview ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">{t('uploadedImage')}</h3>
                    <div className="relative group max-w-md mx-auto">
                      <div className="aspect-square rounded-md overflow-hidden border border-border/50 bg-background/50">
                        <img
                          src={imagePreview}
                          alt="Product preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-md p-8 text-center border-border/50">
                    <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground" />
                    <h3 className="mt-2 text-sm font-medium">{t('noImageUploaded')}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {isEditMode 
                        ? t('uploadNewOrKeepExisting')
                        : t('itemImageDesc')}
                    </p>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => handleTabChange('details')}>
                  {t('back')}
                </Button>
                <Button type="button" onClick={() => handleTabChange('preview')}>
                  {t('btnNextPreviewItem')}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="preview" className="space-y-4">
            <Card className="bg-background border border-border/50 shadow-subtle">
              <CardHeader>
                <CardTitle>{t('itemPreview')}</CardTitle>
                <CardDescription>
                  {t('itemPreviewDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-xs mx-auto">
                  <div className="group cursor-pointer relative">
                    <div className="relative overflow-hidden rounded-lg aspect-[14/16] bg-white">
                      {cardPreview.image ? (
                        <img 
                          alt={cardPreview.name} 
                          className="w-full h-full object-cover transform transition-transform duration-500"
                          src={cardPreview.image}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          <ImageIcon className="h-16 w-16 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex flex-col justify-end pointer-events-none">
                        <div className="absolute bottom-0 left-0 right-0 h-[45%] md:h-[35%] md:group-hover:h-[45%] transition-all duration-300 bg-gradient-to-t from-[rgba(0,0,0,0.85)] via-[rgba(0,0,0,0.5)] to-transparent"></div>
                        <div className="relative">
                          <div className="p-2 sm:p-4 transform translate-y-0 transition-transform duration-300 group-hover:-translate-y-12">
                            <div className="flex items-start justify-between gap-2 sm:gap-3">
                              <h3 className="text-white text-xs sm:text-sm md:text-base font-medium leading-tight flex-1 drop-shadow truncate">
                                {cardPreview.name}
                              </h3>
                            </div>
                            <div className="flex items-center justify-between text-xs sm:text-sm mt-1">
                              <div className="text-white/90 truncate max-w-[40%]">
                                {cardPreview.category}
                              </div>
                              <div className="flex items-center gap-1 sm:gap-3">
                                <div className="text-[#ff9797]">
                                  {cardPreview.quality === 'none' ? '' : cardPreview.quality}
                                </div>
                                <div className="text-white font-medium">
                                  {cardPreview.price}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 px-2 sm:px-4 pb-2 sm:pb-4 opacity-0 translate-y-full group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 pointer-events-auto">
                            <div className="flex gap-2">
                              <a 
                                href={cardPreview.link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="flex-1 bg-white text-black py-1 sm:py-2 rounded text-xs sm:text-sm font-medium text-center hover:bg-gray-100 transition-colors"
                              >
                                {t('checkout')}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <h3 className="text-lg font-semibold">{t('previewDetails')}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('nameLabel')}</span>
                        <span className="font-medium">{cardPreview.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('brandLabel')}</span>
                        <span className="font-medium">{formData.brand || t('notSpecified')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('priceLabel')}</span>
                        <span className="font-medium">{cardPreview.price}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('categoryLabel')}</span>
                        <span className="font-medium text-right">
                          {formData.category || t('notSpecified')}
                        </span>
                      </div>
                      {cardPreview.quality && cardPreview.quality !== 'none' && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{t('batchQualityLabel')}</span>
                          <span className="font-medium">{cardPreview.quality}</span>
                        </div>
                      )}
                      {cardPreview.link && cardPreview.link !== '#' && (
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">
                              {linkConversionError ? t('linkLabel') : t('affiliateLinkLabel')}
                            </span>
                            <a 
                              href={cardPreview.link} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-primary hover:underline flex items-center gap-1"
                            >
                              <span className="max-w-[200px] truncate">
                                {cardPreview.link.replace(/^https?:\/\//, '')}
                              </span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          {cardPreview.originalLink && cardPreview.originalLink !== cardPreview.link && (
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">{t('original')}:</span>
                              <span className="italic text-muted-foreground max-w-[200px] truncate">
                                {cardPreview.originalLink.replace(/^https?:\/\//, '')}
                              </span>
                            </div>
                          )}
                          {linkConversionError && (
                            <div className="flex items-center gap-1 mt-1 text-yellow-600 dark:text-yellow-500 text-xs">
                              <AlertTriangle className="h-3 w-3" />
                              <span>{t('noteAffiliateFailed')}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => handleTabChange('images')}>
                  {t('back')}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="h-4 w-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      {isEditMode ? t('updating') : t('creating')}
                    </>
                  ) : (
                    isEditMode ? t('updateItem') : t('createItem')
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </form>
    </div>
  );
};

export default ItemForm;
